import React, { useEffect, useImperativeHandle, useState, forwardRef } from "react";
import { useInterimTranscript } from "../hooks/useInterimTranscript.js";
import { useRecorder, supportsRecording } from "../hooks/useRecorder.js";
import { holdMic } from "../lib/micGuard.js";
import { readableTranscript } from "../lib/readableTranscript.js";

/**
 * DictatedInput — a text field with a mic inside it.
 *
 * Text first, voice as the option (the Axonify shape, and the Sept-2026 RCT:
 * voice doubles engagement, learns the same as text, costs more — so it is
 * offered, never required). The mic fills the SAME field via browser speech
 * recognition; she reads what it heard and sends it herself. No audio is
 * stored; only the text she confirms leaves the page.
 *
 * Two ways to hear (Oct 16). Given a `clientId`, the mic RECORDS and asks the
 * server to transcribe — the voice interview's path, which works on every
 * phone. The browser's own recognition, where present, runs alongside and
 * previews words as they come; where it is known to be reliable (Chrome and
 * Edge on desktop and Android — a real engine, whole-utterance results) its
 * final text is used at once and the recording is discarded, so there is no
 * wait. On iPhones and iPads (every browser there is WebKit, which stops
 * after the first pause and needs Siri & Dictation on) the recording is the
 * answer. Without a clientId it is the browser's recognition alone, and
 * where that is unsupported the mic is simply absent. The recording is not
 * kept: the server deletes it once transcribed, and only the text she
 * confirms leaves the page.
 *
 * What it heard is made readable as it arrives (Plan H): spoken "comma" and
 * "period" become marks, sentences get capitals. Words are never changed.
 * onChange gets a second argument, { dictated: true }, for dictated text, so
 * a form can ask the server for the AI punctuation pass on save (Plan H2).
 */
const DictatedInput = forwardRef(function DictatedInput({
  value, onChange, onSubmit, placeholder, ariaLabel, disabled, autoFocus, style, clientId = null, onFocus = null,
}, ref) {
  const dict = useInterimTranscript();
  const recordMode = !!clientId && supportsRecording();
  // Trust the browser's final transcript on engines that deliver one; never
  // on iOS, where every browser is WebKit and the result is a fragment.
  const browserIsTrusted = dict.supported && !/iP(hone|ad|od)/.test(navigator.userAgent)
    && /Chrome|Chromium|Edg/.test(navigator.userAgent);
  const rec = useRecorder({ onAutoStop: blob => finishRecording(blob) });
  const [listening, setListening] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [micError, setMicError] = useState(null);
  const micShown = recordMode || dict.supported;

  // While listening, the transcript IS the field value.
  useEffect(() => {
    if (listening && dict.interim) onChange(readableTranscript(dict.interim), { dictated: true });
  }, [listening, dict.interim]);  // eslint-disable-line react-hooks/exhaustive-deps

  async function finishRecording(blob) {
    setListening(false);
    if (dict.supported) dict.stop();
    if (!blob || blob.size === 0) return;
    // A trusted engine already heard the whole thing: use its words, skip
    // the round trip, and let the recording go unsent.
    if (browserIsTrusted && (dict.final || "").trim()) {
      onChange(readableTranscript(dict.final), { dictated: true });
      return;
    }
    setTranscribing(true); setMicError(null);
    try {
      const form = new FormData();
      const ext = (blob.type.split("/")[1] || "webm").split(";")[0];
      form.append("file", blob, `dictation.${ext}`);
      const r = await fetch(`/api/clients/${clientId}/dictation`, { method: "POST", body: form });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || `Couldn't transcribe (${r.status})`);
      if (d.error) { setMicError(d.error); return; }
      if (d.text) onChange(d.text, { dictated: true });
      else if (!value) setMicError("Didn't catch that — try again, or type.");
    } catch (e) {
      setMicError(e.message || "Couldn't transcribe — type instead.");
    } finally {
      setTranscribing(false);
    }
  }

  async function toggleMic() {
    setMicError(null);
    if (recordMode) {
      if (listening) { const blob = await rec.stop(); await finishRecording(blob); return; }
      try { await rec.start(); }
      catch { setMicError("Microphone not available — type instead."); return; }
      if (dict.supported) { dict.reset(); dict.start(); }
      setListening(true);
      return;
    }
    if (listening) { dict.stop(); setListening(false); return; }
    dict.reset(); dict.start(); setListening(true);
  }
  function stopAll() {
    if (!listening) return;
    if (recordMode) rec.cancel();
    dict.stop(); setListening(false);
  }
  // The host's own Send / Save / Skip buttons must close the mic too — the
  // Ask bar's arrow left recognition running after the question went (David,
  // Oct 16: the car thought a call was on). The mic is also released when the
  // field is disabled (the host is busy sending) and on unmount.
  useImperativeHandle(ref, () => ({ stop: stopAll }));
  useEffect(() => { if (disabled) stopAll(); }, [disabled]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { rec.cancel(); dict.stop(); }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  // While listening: the page leaving the front, or 60 s passing, ends the
  // turn here too — the hooks release the mic on their own, but the field
  // must stop saying "Listening…" when nothing is.
  useEffect(() => {
    if (!listening) return;
    const off = holdMic(stopAll);
    const cap = setTimeout(stopAll, 60 * 1000);
    return () => { off(); clearTimeout(cap); };
  }, [listening]);  // eslint-disable-line react-hooks/exhaustive-deps

  function key(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (listening && recordMode) { toggleMic(); return; }   // stop → transcribe; she submits after reading it
      stopAll();
      onSubmit?.();
    }
  }

  return (
    <div style={{ ...s.wrap, ...(style || {}) }}>
      <input
        style={s.input}
        value={value}
        placeholder={transcribing ? "One moment, transcribing…" : listening ? (recordMode ? "Listening… tap the mic when done" : "Listening…") : placeholder}
        aria-label={ariaLabel || placeholder}
        disabled={disabled || transcribing}
        autoFocus={autoFocus}
        onChange={e => { stopAll(); onChange(e.target.value); }}
        onKeyDown={key}
        onFocus={onFocus || undefined}
      />
      {micError && <span role="status" style={s.micError}>{micError}</span>}
      {micShown && (
        <button type="button" onClick={toggleMic} disabled={disabled || transcribing}
                aria-pressed={listening}
                aria-label={listening ? "Stop listening" : "Speak instead of typing"}
                style={{ ...s.mic, ...(listening ? s.micOn : {}), ...(transcribing ? s.micBusy : {}) }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
               strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <rect x="9" y="3" width="6" height="11" rx="3" />
            <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
          </svg>
        </button>
      )}
    </div>
  );
});
export default DictatedInput;

const s = {
  wrap:  { display: "flex", alignItems: "center", gap: 6, background: "var(--color-surface)", border: "1px solid var(--color-border-strong)", borderRadius: "var(--radius-lg)", padding: "6px 6px 6px 12px" },
  input: { flex: 1, minWidth: 0, font: "inherit", fontSize: "var(--text-base)", border: "none", outline: "none", background: "transparent", color: "var(--color-text-primary)" },
  mic:   { width: 36, height: 36, borderRadius: 999, border: "none", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  micOn: { background: "var(--color-accent-subtle)", color: "var(--color-accent)" },
  micBusy: { opacity: 0.5 },
  micError: { fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 160 },
};
